<template>
	<VueFormMultiselect
		ref="multiselectRef"
		v-model="multiselectValue"
		:mode="props.mode"
		:options="visibleOptions"
		:placeholder="props.placeholder"
		:searchable="props.searchable"
		:close-on-select="props.mode === MultiselectMode.SINGLE"
		:no-results-text="props.noResultsText"
		:class="{
			'hasClosedPlaceholder': showClosedPlaceholder
		}"
		:style="{ '--ms-closed-placeholder': closedPlaceholder }"
		:loading="props.loading"
		class="Multiselect"
		:canClear="canClear"
		@open="handleOpen"
		@close="handleClose"
		@search-change="handleSearchChange"
	>
		<template #nooptions>
			<p class="MultiselectNoOptions" :class="{ 'isLoading': props.loading  }">
				<span v-if="props.loading" class="multiselect-spinner" aria-hidden="true"></span>
				<span v-if="props.loading">Searching...</span>
				<span v-else>{{ showOptions ? props.noOptionsText : props.startTypingText }}</span>
			</p>
		</template>
		<template #option="{ option, isSelected }">
			<div class="SelectItemWrapper">
				<SelectItem
					:item="mapOptionToSelectItem(option, isSelected(option))"
					:type="selectItemType"
				>
					<template v-if="option.icon" #icon>
						<i :class="`Icon ${option.icon}`" aria-hidden="true"></i>
					</template>
				</SelectItem>
			</div>
		</template>

		<template #singlelabel="{ value }">
			<div class="MultiselectSingleLabel" :class="{ isPlaceholder: showPlaceholderInSingleLabel }">
				<i v-if="value.icon" :class="`Icon ${value.icon}`" aria-hidden="true"></i>
				<span>{{ showPlaceholderInSingleLabel ? props.placeholder : value.label }}</span>
			</div>
		</template>

		<template v-if="props.mode === MultiselectMode.TAGS" #tag="{ option, handleTagRemove }">
			<Chip
				:key="option.value"
				:label="option.label"
				@remove="(e: Event) => handleTagRemove(option, e)"
			/>

		</template>

		<template #caret>
			<i
				class="CaretIcon"
				:class="isOpen ? 'lnr-chevron-up' : 'lnr-chevron-down'"
				aria-hidden="true"
				@mousedown.prevent.stop="toggleDropdown"
			></i>
		</template>

		<template v-if="canClear" #clear="{ clear }">
			<i
				class="ClearIcon lnr-cross"
				aria-hidden="true"
				@mousedown.prevent.stop="clear"
			></i>
		</template>
	</VueFormMultiselect>
	<p v-if="props.description" class="MultiselectDescription">{{ props.description }}</p>
	<p v-if="props.warning" class="MultiselectWarning">{{ props.warning }}</p>
</template>

<script setup lang="ts">
import { computed, defineAsyncComponent, ref } from 'vue';
const VueFormMultiselect = defineAsyncComponent(() => import('@vueform/multiselect'));
import '@vueform/multiselect/themes/default.css';
import SelectItem from '@components/SelectItem/SelectItem.vue';
import { useMultiselect } from '@composables/useMultiselect';
import { multiselectPropsDefinition } from '@app/interfaces/useMultiselect.interface';
import { MultiselectMode } from '@app/enums/MultiselectMode';
import Chip from '@components/TagsElement/Chip.vue';

const props = defineProps(multiselectPropsDefinition);


const emit = defineEmits<{
	'updateValues': [value: string | number | (string | number)[] | null];
	'update:modelValue': [value: string | number | (string | number)[] | null];
	'open': [];
	'close': [];
	'searchChange': [value: string];
}>();

const multiselectRef = ref<InstanceType<typeof VueFormMultiselect> | null>(null);
const {
	multiselectValue,
	isOpen,
	selectItemType,
	mapOptionToSelectItem,
	searchQuery,
	showOptions,
	visibleOptions,
	handleSearchChange,
	hasSelection,
	onOpen,
	onClose
} = useMultiselect({ props, emit });

const showPlaceholderInSingleLabel = computed(() => (
	props.alwaysShowPlaceholder || !isOpen.value
));

const showClosedPlaceholder = computed(() => (
	props.mode === MultiselectMode.TAGS &&
	hasSelection.value &&
	!isOpen.value
));

const closedPlaceholder = computed(() => JSON.stringify(props.placeholder));

const handleOpen = (): void => {
	onOpen();
};

const handleClose = (): void => {
	searchQuery.value = '';
	onClose();
};

const toggleDropdown = () => {
	const multiselect = multiselectRef.value as any;
	if (multiselect?.isOpen) {
		multiselect?.close();
		multiselect?.deactivate();
	} else {
		multiselect?.open();
		multiselect?.activate();
	}
};

</script>

<style scoped lang="scss">
.Multiselect {
	width: 100%;

	@include PaddingAreas(0, 0.25);

	--ms-font-size: 1rem;
	--ms-font-weight: 400;
	--ms-bg: #{$White};
	--ms-border-color: #{$Grey-600};
	--ms-border-width: 1px;
	--ms-radius: 0.25rem;
	--ms-ring-width: 0;
	--ms-ring-color: transparent;
	--ms-placeholder-color: #{$Grey-600};
	--ms-caret-color: #{$Grey-600};
	--ms-clear-color: #{$Grey-600};
	--ms-clear-color-hover: #{$Grey-900};
	--ms-tag-my: 0;
	--ms-px: 0;

	// Dropdown
	--ms-max-height: v-bind('props.maxHeight');
	--ms-dropdown-bg: #{$White};
	--ms-dropdown-border-color: #{$Grey-600};
	--ms-dropdown-border-width: 1px;
	--ms-dropdown-radius: 0.25rem;

	// Options
	--ms-option-font-size: 1rem;
	--ms-option-line-height: 1rem;
	--ms-option-bg-pointed: transparent;
	--ms-option-color-pointed: #{$Grey-900};
	--ms-option-bg-selected: transparent;
	--ms-option-bg-selected-pointed: transparent;
	--ms-option-color-selected: #{$Grey-900};
	--ms-option-color-selected-pointed: #{$Grey-900};
	--ms-option-py: 0.25rem;
	--ms-option-px: 0.5rem;

	&.is-active {
		outline: 2px solid #{$Blue-600};
	}

	&:not(.is-open) {
		.MultiSelectSingleLabel span {
			display: none;
		}

		:deep(.multiselect-tags) {
			.Chip {
				display: none;
			}
			.multiselect-tags-search-wrapper > .multiselect-tags-search {
				color: $Grey-900;
			}
		}
	}
	&.hasClosedPlaceholder {
		:deep(.multiselect-wrapper) {
			position: relative;
		}

		:deep(.multiselect-wrapper)::after {
			content: var(--ms-closed-placeholder);
			color: $Grey-600;
			position: absolute;
			left: 0.5rem;
			right: 2.5rem;
			top: 50%;
			transform: translateY(-50%);
			pointer-events: none;
			white-space: nowrap;
			overflow: hidden;
			text-overflow: ellipsis;
		}

		:deep(.multiselect-tags-search) {
			color: transparent;
			caret-color: transparent;

		}
	}
}

.MultiselectSingleLabel {
	display: flex;
	align-items: center;
	gap: 0.5rem;
	position: absolute;
	top: 0;
	width: 100%;
	height: 100%;
	pointer-events: none;

	.Icon {
		width: 1rem;
		height: 1rem;
		flex-shrink: 0;
	}

	&.isPlaceholder {
		color: $Grey-600;
	}
}

.CaretIcon {
	color: $Grey-600;
	z-index: $FirstFloor;
	cursor: pointer;

	@include MarginAreas(0, 0.5, 0, 0);
}

.ClearIcon {
	color: $Grey-600;
	cursor: pointer;

	@include MarginAreas(0, 0.5, 0, 0);

	&:hover {
		color: $Grey-900;
	}
}

:deep(.multiselect-tags-search-wrapper) {
	width: 100%;

	@include MarginAreas(0);
}

:deep(.multiselect-tags) {
	gap: 0.5rem;

	@include PaddingAreas(0);

	div:last-child:not(:only-child) {
		@include PaddingAreas(0.5, 0, 0, 0);
	}
}

:deep(.multiselect-wrapper) {
	font-size: 1rem;
	line-height: 1rem;
	font-weight: 400;
	align-items: end;

	@include PaddingAreas(1, 0);
}

:deep(.multiselect-dropdown) {
	scrollbar-width: none;
	-ms-overflow-style: none;

	ul li {
		@include MarginAreas(0);
	}

	&::-webkit-scrollbar {
		display: none;
	}
}


.SelectItemWrapper {
	width: 100%;
	pointer-events: none;

	:deep(.SelectItem) {
		max-width: 100%;

		@include PaddingAreas(0.5);
	}
}

:deep(.multiselect-option) {
	@include PaddingAreas(0);

	&:hover .SelectItemWrapper .SelectItem {
		background: $Blue-100;
	}
}

:deep(.multiselect-spinner) {
	@include MarginAreas(0, 0.5, 0, 0);
}

:deep(.multiselect-no-results) {
	color: $Grey-900;
	@include TextStyle(BodyMediumRegular);
	@include PaddingAreas(1, 0.25);
}

.ChipsList {
	display: flex;
	flex-wrap: wrap;
	gap: 0.5rem;

	@include PaddingAreas(0.25, 0);
}

.MultiselectDescription {
	color: $Grey-700;

	@include TextStyle(BodyMediumRegular);
	@include MarginAreas(0.5, 0, 0, 0);
}

.MultiselectWarning {
	color: $Red-500;

	@include TextStyle(BodyMediumRegular);
	@include MarginAreas(0.5, 0, 0, 0);
}

.MultiselectNoOptions {
	color: $Grey-900;
	@include TextStyle(BodyMediumRegular);
	@include PaddingAreas(0.5, 0.25);
	@include MarginAreas(0);

	&.isLoading {
		display: flex;
		align-items: center;
		gap: 0.5rem;
	}
}
</style>
